'use client';

import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useEffect, useId, useRef, useState } from 'react';
import { SwiperBox } from './styled';
import { HousingCardGallerySlide } from './HousingCardGallerySlide';
import noImage from '@/assets/webp/no-image.webp';
import { MediaFile } from '@/types';

interface GallerySwiperProps {
  id: string;
  imageList: Array<{
    imageFullUrl: string;
    imageMidUrl: string;
    imageTmbUrl: string;
  }> | null;
  className?: string;
  link: string;
  basePath?: string;
  images?: MediaFile[];
  priority?: boolean;
  cardIndex?: number;
  onLinkClick?: () => void;
}

const buildSlides = (
  list: Array<{ imageFullUrl: string; imageMidUrl: string; imageTmbUrl: string }> | null,
) =>
  (list ?? []).slice(0, 5).map((img) => ({
    src: img.imageMidUrl || img.imageFullUrl || img.imageTmbUrl || '',
    loaded: true,
  }));

export const HousingCardGallerySwiper = ({
  id,
  className,
  imageList,
  link,
  basePath = 'housing',
  priority,
  cardIndex,
  onLinkClick,
}: GallerySwiperProps) => {
  const resolvedIndex = cardIndex ?? (priority ? 0 : 99);
  const isAboveFold = resolvedIndex < 3;

  const [slides, setSlides] = useState(() => buildSlides(imageList));

  const uniqueId = useId();
  const initializedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!imageList?.length) return;
    if (initializedIdRef.current === id) return;
    initializedIdRef.current = id;
    setSlides(buildSlides(imageList));
  }, [id, imageList]);

  return (
    <SwiperBox className={className || ''} $noImageSrc={noImage.src}>
      <Swiper
        style={{ height: '100%', overflow: 'hidden' }}
        className={className}
        modules={[Navigation, Pagination]}
        navigation={{
          nextEl: `.${uniqueId}-next`,
          prevEl: `.${uniqueId}-prev`,
        }}
        pagination={{ el: `.${uniqueId}-custom-pagination`, clickable: true }}
      >
        {slides?.map((slide, idx) => (
          <SwiperSlide key={idx} style={{ width: '100%', height: '100%' }}>
            <HousingCardGallerySlide
              src={slide.src}
              link={link}
              basePath={basePath}
              fetchPriority={idx === 0 ? (isAboveFold ? 'high' : 'auto') : 'low'}
              loading={idx === 0 ? (isAboveFold ? 'eager' : 'lazy') : 'lazy'}
              onLinkClick={onLinkClick}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <button className={`custom-prev ${uniqueId}-prev`} aria-label="Previous slide">
        <span aria-hidden="true">‹</span>
      </button>
      <button className={`custom-next ${uniqueId}-next`} aria-label="Next slide">
        <span aria-hidden="true">›</span>
      </button>
      {slides && slides?.length > 1 && (
        <div className={`custom-pagination ${uniqueId}-custom-pagination`}></div>
      )}
    </SwiperBox>
  );
};
